import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'

export type LimitStatus = 'ok' | 'warning' | 'critical' | 'not_connected' | 'unavailable'

export interface ServiceLimit {
  key: string
  service: string
  label: string
  status: LimitStatus
  value: string
  detail: string | null
  how_to: string | null
  used: number | null
  limit: number | null
  percent: number | null
}

interface LimitsResponse {
  data: ServiceLimit[]
  meta: { checked_at: string }
}

const limitsKey = ['service-limits'] as const

/** Owners: how close Arva SEO is to each provider's limit (refreshed every 10 minutes on the server). */
export function useServiceLimits() {
  return useQuery({ queryKey: limitsKey, queryFn: () => http.get<LimitsResponse>('/limits') })
}

export function useRefreshLimits() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => http.get<LimitsResponse>('/limits', { fresh: 1 }),
    onSuccess: (data) => queryClient.setQueryData(limitsKey, data),
  })
}

export interface CspReportRow {
  app: 'app' | 'cp'
  directive: string
  blocked: string
  page: string
  count: number
  first_seen_at: string
  last_seen_at: string
}

interface CspReportsResponse {
  data: CspReportRow[]
  meta: { total: number; keep_days: number }
}

/** Owners: what the app's and control panel's Content-Security-Policy blocks, as browsers report it. */
export function useCspReports() {
  return useQuery({ queryKey: ['csp-reports'], queryFn: () => http.get<CspReportsResponse>('/csp-reports') })
}

export type SpendDays = 7 | 30 | 90

export interface SpendWorkspace {
  id: string
  name: string
  plan: string
  cost: number
  credits?: number
}

export interface DataSpend {
  days: SpendDays
  from: string
  to: string
  /** US dollars, as each provider charged. */
  total: number
  previous_total: number
  calls: number
  series: { date: string; by_provider: Record<string, number>; total: number }[]
  providers: { key: string; label: string; cost: number; calls: number }[]
  operations: { key: string; label: string; provider: string; cost: number; calls: number }[]
  plans: { plan: string; cost: number }[]
  workspaces: SpendWorkspace[]
  ai_credits: {
    credits: number
    cost: number
    uses: number
    workspaces: number
    actions: { key: string; label: string; credits: number; cost: number; uses: number; workspaces: number }[]
    top_workspaces: SpendWorkspace[]
    own_key_workspaces: number
  }
}

/** Owners: what paid data calls cost by day, and what customers use their AI credits for. */
export function useDataSpend(days: SpendDays) {
  return useQuery({
    queryKey: ['data-spend', days],
    queryFn: () => http.get<{ data: DataSpend }>('/limits/data-spend', { days }),
    placeholderData: keepPreviousData,
  })
}

export type KnownFileVerdict = 'malware' | 'safe' | 'review'

export interface KnownFile {
  sha256: string
  verdict: KnownFileVerdict
  source: 'clamav' | 'rules' | 'patterns' | 'staff' | 'expected'
  name: string | null
  excerpt: string | null
  sites: number
  first_seen_at: string
  last_seen_at: string
  decided_by: string | null
  decided_at: string | null
}

/** A line added to changed files on connected websites (plugin 2.7.0+), by its fingerprint. */
export interface LearnedLine {
  fingerprint: string
  line: string
  verdict: 'review' | 'malicious' | 'safe'
  source: 'changed_file' | 'file_edit'
  risky: boolean
  sites: number
  first_seen_at: string
  last_seen_at: string
  decided_by: string | null
  decided_at: string | null
}

/** A host WordPress sites load scripts from (plugin 2.7.0+), with its checks. */
export interface ScriptHost {
  host: string
  verdict: 'known' | 'bad' | 'review'
  source: 'auto' | 'checks' | 'staff' | null
  name: string | null
  sites: number
  registered_at: string | null
  hides: boolean | null
  hides_detail: string | null
  web_risk: 'clean' | 'flagged' | null
  web_risk_threats: string[]
  last_seen_at: string
  decided_by: string | null
  decided_at: string | null
}

/** What the scanner learned in the last 7 days. */
export interface LearnedThisWeek {
  malware: number
  patterns: number
  bad_hosts: number
  safe: number
  known_hosts: number
  known_hosts_auto: number
  false_alarms: number
  to_review: number
}

interface SecurityFeedResponse {
  data: KnownFile[]
  lines: LearnedLine[]
  hosts: ScriptHost[]
  meta: {
    counts: Record<KnownFileVerdict, number>
    version: string
    curated_version: string
    patterns: number
    php: Record<string, string> | null
    review_sites: number
    expected_sites: number
    line_sites: number
    known_sites: number
    week: LearnedThisWeek
  }
}

const securityFeedKey = ['security-feed'] as const

/** Owners: what the security scanner has learned from connected websites and from Arva SEO. */
export function useSecurityFeed() {
  return useQuery({
    queryKey: securityFeedKey,
    queryFn: () => http.get<SecurityFeedResponse>('/security-feed'),
  })
}

/** Mark a file as malware or safe, or add a fingerprint: every website's next scan uses it. */
export function useDecideKnownFile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { sha256: string; verdict: 'malware' | 'safe'; name?: string }) =>
      http.post<{ data: KnownFile }>('/security-feed/decisions', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: securityFeedKey }),
  })
}

/** Mark an added line as malicious (a pattern for every site) or safe (no longer reported). */
export function useDecideLine() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { fingerprint: string; verdict: 'malicious' | 'safe' }) =>
      http.post<{ data: LearnedLine }>('/security-feed/lines/decisions', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: securityFeedKey }),
  })
}

/** Mark a script host as known (not checked again) or bad (flagged on every site that loads it). */
export function useDecideHost() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { host: string; verdict: 'known' | 'bad'; name?: string }) =>
      http.post<{ data: ScriptHost }>('/security-feed/hosts/decisions', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: securityFeedKey }),
  })
}
